import { Editor, Extension } from '@tiptap/react';
import {
  getIndent,
  isBetweenText,
  isFirstListItem,
  indentEntireList,
  createIndentCommand,
  removeListItemIndent,
  restoreListItemIndent,
  findCurrentListItem,
} from '@/utils/editor-utils';
import { IndentConfig } from './indent-config';

export interface IndentOptions {
  types: string[];
  minIndent: number;
  maxIndent: number;
  baseStepPx: number;
}

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType;
      outdent: () => ReturnType;
    };
  }
}

const handleIndent = (editor: Editor, delta: number, key?: string) => {
  const isIncrease = delta === IndentConfig.more;

  if (isBetweenText(editor) && key !== 'Backspace') {
    editor.commands.insertContent(' ');
    return true;
  }

  const listCtx = findCurrentListItem(editor);

  if (listCtx) {
    const listItemIndent = getIndent(listCtx.node);

    const canSink = isIncrease && editor.can().sinkListItem('listItem');
    const canLift = !isIncrease && editor.can().liftListItem('listItem') && listItemIndent === 0;

    if (canSink || canLift) {
      const didChange = isIncrease ? editor.commands.sinkListItem('listItem') : editor.commands.liftListItem('listItem');

      if (!didChange) return false;

      if (isIncrease) {
        removeListItemIndent(editor);
      } else {
        requestAnimationFrame(() => {
          restoreListItemIndent(editor);
        });
      }

      return true;
    }

    if (isFirstListItem(editor)) {
      return indentEntireList(editor, delta);
    }

    return isIncrease ? editor.commands.indent() : editor.commands.outdent();
  }

  const didIndent = isIncrease ? editor.commands.indent() : editor.commands.outdent();

  if (key === 'Backspace') {
    return false;
  }

  !didIndent && editor.commands.focus();

  return true;
};

export const Indent = Extension.create<IndentOptions>({
  name: 'indent',

  addOptions() {
    return {
      types: ['paragraph', 'heading', 'listItem'],
      minIndent: IndentConfig.min,
      maxIndent: IndentConfig.max,
      baseStepPx: 30,
    };
  },

  addGlobalAttributes() {
    const step = this.options.baseStepPx ?? 30;

    const indentAttr = {
      default: 0,
      parseHTML: (el: Element) => {
        const raw = el.getAttribute('data-indent');
        return (raw ? parseInt(raw, 10) : 0) || 0;
      },
    };

    return [
      {
        types: ['paragraph', 'heading'],
        attributes: {
          indent: {
            ...indentAttr,
            renderHTML: (attrs: { indent?: number }) => {
              const indent = Number(attrs.indent) || 0;

              if (!indent) {
                return {};
              }

              return { 'data-indent': indent, style: `text-indent: 30px; margin-left: min(${(indent - 1) * step}px, 70rem);` };
            },
          },
        },
      },

      {
        types: ['listItem'],
        attributes: {
          indent: {
            ...indentAttr,
            renderHTML: (attrs: { indent?: number }) => {
              const indent = Number(attrs.indent) || 0;
              if (!indent) {
                return {};
              }

              return { 'data-indent': indent, style: `margin-left: min(${indent * step}px, 70rem);` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent: () => {
        return createIndentCommand({
          delta: IndentConfig.more,
          types: this.options.types,
        });
      },
      outdent: () => {
        return createIndentCommand({
          delta: IndentConfig.less,
          types: this.options.types,
        });
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => handleIndent(this.editor, IndentConfig.more),
      'Shift-Tab': () => handleIndent(this.editor, IndentConfig.less),
      Backspace: () => {
        const { state } = this.editor;
        const { from, to } = state.selection;

        const hasTextBeforeCursor = from > 1 && state.doc.textBetween(from - 1, from).length > 0;
        const hasSelection = from !== to;

        if (hasTextBeforeCursor || hasSelection) return false;

        return handleIndent(this.editor, IndentConfig.less, 'Backspace');
      },
    };
  },
});
